/**
 * useBorderToggle - Hook for managing area border visibility state
 *
 * Manages the border toggle state with URL synchronization and browser history support.
 * When border is toggled ON after viewing a detected area, fetches the boundary for
 * that area to enable a smooth transition without page reload.
 *
 * @example
 * ```tsx
 * const {
 *   showBorder,
 *   handleToggleBorder,
 *   dynamicEncodedBoundary,
 * } = useBorderToggle({
 *   router,
 *   originalArea: area,
 * });
 *
 * // Use in MapComponent
 * <MapComponent
 *   showBoundary={showBorder}
 *   encodedBoundary={dynamicEncodedBoundary || boundaryData.boundary.coordinates}
 * />
 *
 * // Use in toggle button - pass detectedArea at call time
 * <BorderToggleButton isChecked={showBorder} onClick={() => handleToggleBorder(detectedArea)} />
 * ```
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getAreaScopeKey, getEncodedBoundary, type AreaResponse } from '@/src/lib/api/endpoints/area';
import type { CompatRouter } from '@/src/lib/router-compat/useCompatRouter';
import { useMapBorderState, useMapDetectedArea } from './useMapState';
import { stripSrpPageSegment } from '@/src/lib/seo/srp-pagination';

export type UseBorderToggleParams = {
  /** Next.js router instance */
  router: CompatRouter;
  /** Original area from URL route */
  originalArea: AreaResponse | undefined;
};

export type UseBorderToggleResult = {
  /** Whether border is currently visible */
  showBorder: boolean;
  /** Toggle border visibility (handles URL and history). Pass detectedArea to fetch its boundary when toggling ON. */
  handleToggleBorder: (detectedArea?: AreaResponse) => Promise<void>;
  /** Dynamically fetched boundary for detected area (when toggling border ON). MultiPolygon encoded coordinates. */
  dynamicEncodedBoundary: Array<Array<string>> | null;
};

/**
 * Manages area border visibility with URL sync and browser history
 */
export function useBorderToggle({ router, originalArea }: UseBorderToggleParams): UseBorderToggleResult {
  const originalAreaKey = originalArea ? getAreaScopeKey(originalArea) : undefined;
  // Border toggle state - controls whether area boundary is visible on map
  // Synced with URL query parameter ?border=false
  const [showBorder, setShowBorder] = useState<boolean>(true);

  // Dynamic boundary for detected areas (when toggling border back on)
  // Type is string[][] for MultiPolygon encoded coordinates
  const [dynamicEncodedBoundary, setDynamicEncodedBoundary] = useState<Array<Array<string>> | null>(null);

  // Sync showBorder state with URL query parameter
  // Enables browser back/forward navigation and shareable URLs
  useEffect(() => {
    const syncFromURL = () => {
      const borderParam = router.query.border;
      const newShowBorder = borderParam !== 'false'; // Default to true (show border)
      setShowBorder(newShowBorder);
    };

    syncFromURL();

    // Listen for browser navigation (back/forward)
    const handleRouteChange = () => {
      syncFromURL();
    };

    // Listen for popstate events (back/forward button clicks)
    const handlePopState = () => {
      // Parse border param from current URL
      const params = new URLSearchParams(window.location.search);
      const borderParam = params.get('border');
      const newShowBorder = borderParam !== 'false';
      setShowBorder(newShowBorder);
    };

    router.events?.on('routeChangeComplete', handleRouteChange);
    window.addEventListener('popstate', handlePopState);

    return () => {
      router.events?.off('routeChangeComplete', handleRouteChange);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [router.query.border, router.events]);

  // Sync from RQ cache when layout's border toggle writes a change.
  // The layout's BorderToggleButton writes to the RQ cache via useSetMapBorderState.
  // This effect detects that change and syncs local state + URL to match.
  // Also fetches the detected area's boundary when toggling ON (same logic as handleToggleBorder).
  const cacheBorderState = useMapBorderState();
  const detectedAreaFromCache = useMapDetectedArea();
  const prevCacheShowBorderRef = useRef(cacheBorderState.showBorder);

  useEffect(() => {
    const cacheValue = cacheBorderState.showBorder;
    if (cacheValue !== prevCacheShowBorderRef.current) {
      prevCacheShowBorderRef.current = cacheValue;

      if (cacheValue !== showBorder) {
        setShowBorder(cacheValue);

        // When toggling ON with a detected area different from original, fetch its boundary
        if (cacheValue && detectedAreaFromCache && getAreaScopeKey(detectedAreaFromCache) !== originalAreaKey) {
          getEncodedBoundary(detectedAreaFromCache)
            .then((response) => {
              if (response.boundary?.coordinates) {
                setDynamicEncodedBoundary(response.boundary.coordinates);
              }
              return undefined;
            })
            .catch(() => {
              // Non-critical — border toggle still works without the dynamic boundary
            });
        } else if (!cacheValue) {
          setDynamicEncodedBoundary(null);
        }

        // Sync URL to match
        const params = new URLSearchParams(window.location.search);
        params.delete('page');
        if (cacheValue) {
          params.delete('border');
        } else {
          params.set('border', 'false');
        }
        const queryString = params.toString();
        const pathname = stripSrpPageSegment(window.location.pathname);
        const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
        window.history.pushState(null, '', newUrl);
      }
    }
  }, [cacheBorderState.showBorder, showBorder, detectedAreaFromCache, originalAreaKey]);

  // Border toggle handler
  // detectedArea is passed as argument (not prop) to break circular dependency with useDynamicAreaDetection
  const handleToggleBorder = useCallback(
    async (detectedArea?: AreaResponse) => {
      const newValue = !showBorder;

      // If turning border ON and we have a detected area different from original,
      // fetch the boundary for the detected area (smooth transition, no page reload)
      if (newValue && detectedArea && getAreaScopeKey(detectedArea) !== originalAreaKey) {
        try {
          const boundaryResponse = await getEncodedBoundary(detectedArea);
          if (boundaryResponse.boundary?.coordinates) {
            setDynamicEncodedBoundary(boundaryResponse.boundary.coordinates);
          }
        } catch {
          // Boundary fetch is non-critical - border toggle still works, just without the dynamic boundary
          // The map will continue showing the original area's boundary
        }
      } else if (!newValue) {
        // Clear dynamic boundary when turning border OFF
        setDynamicEncodedBoundary(null);
      }

      setShowBorder(newValue);

      // Update URL query parameter
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      if (newValue) {
        params.delete('border'); // Remove parameter when showing border (default)
      } else {
        params.set('border', 'false'); // Add parameter when hiding border
      }

      const queryString = params.toString();
      const pathname = stripSrpPageSegment(window.location.pathname);
      const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
      // Use pushState (not replaceState) to add to browser history
      // This allows users to navigate back/forward through border state changes
      window.history.pushState(null, '', newUrl);
    },
    [showBorder, originalAreaKey],
  );

  return {
    showBorder,
    handleToggleBorder,
    dynamicEncodedBoundary,
  };
}
